import {
  Box,
  Button,
  Divider,
  Flex,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import { YIELD_TEXT_STRINGS } from "@components/VaultYield/contants";
import { StrategyAllocationType } from "@constants/types";
import Image from "next/image";
import { useEffect, useMemo, useState } from "react";
import { FiChevronDown } from "react-icons/fi";

const TEXT_STRINGS = YIELD_TEXT_STRINGS.SECTIONS.PORTFOLIO.APR_REPORTS;

type Allocation = {
  allocation: string;
  apr_on_position: string;
  apr_on_total: string;
  key: string;
  strategy_name: string;
  img_url?: string;
};

export type CMSAllocation = {
  strategy_start_date: string;
  strategy_end_date: string;
  allocations: Allocation[];
};

const VaultAprReports = ({
  cmsAllocations,
}: {
  cmsAllocations: CMSAllocation[];
}) => {
  const [selectedDate, setSelectedDate] = useState("");

  const dates = useMemo(() => {
    return cmsAllocations.reverse().map((data) => {
      return {
        key: `${data.strategy_start_date}_${data.strategy_end_date}`,
        value: `${data.strategy_start_date} - ${data.strategy_end_date}`,
      };
    });
  }, [cmsAllocations]);

  useEffect(() => {
    if (dates.length > 0) {
      setSelectedDate(dates[0].key);
    }
  }, [dates]);

  const updatedAllocations = useMemo(() => {
    return cmsAllocations.reduce((acc: any, curr) => {
      const key = `${curr.strategy_start_date}_${curr.strategy_end_date}`;
      if (acc[key]) {
        acc[key] = acc[key].push(curr);
      } else {
        acc[key] = [curr];
      }
      return acc;
    }, {});
  }, [cmsAllocations]);

  const selectedDateHistory = useMemo(() => {
    const allocationsData = updatedAllocations[selectedDate];
    if (allocationsData) {
      return {
        allocations: allocationsData?.[0]?.allocations,
        note: allocationsData?.[0]?.note || "",
        avgAPY: allocationsData?.[0]?.apy || "",
      };
    }
    return {
      allocations: [],
      note: "",
      avgAPY: "",
    };
  }, [updatedAllocations, selectedDate]);

  const {
    allocations,
    note,
    avgAPY,
  }: { allocations: StrategyAllocationType[]; note: string; avgAPY: string } =
    selectedDateHistory;

  const selectedDateObj = useMemo(() => {
    return dates.find((dateObj) => dateObj.key === selectedDate);
  }, [dates, selectedDate]);

  return (
    <Flex direction={"column"} p="8px 8px 16px 8px" gap="24px">
      <Flex
        alignItems="center"
        gap="24px"
        justifyContent={{ base: "space-between", md: "flex-start" }}
      >
        <Flex alignItems="center" gap="8px" color="textSecondary">
          <Text fontSize={{ base: "10px", md: "14px" }} fontWeight="400">
            Period:
          </Text>
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<FiChevronDown />}
              p={{ base: "4px 12px", md: "8px 12px" }}
              bg="#D8F0F0"
              borderRadius="8px"
              _active={{
                bg: "#D8F0F0",
              }}
              _hover={{
                bg: "#D8F0F0",
              }}
            >
              <Text
                fontSize={{ base: "12px", md: "14px" }}
                lineHeight="18px"
                fontWeight="400"
                color="textPrimary"
              >
                {selectedDateObj?.value}
              </Text>
            </MenuButton>
            <MenuList
              py="12px"
              bg="#E8FFFF"
              borderRadius="8px"
              border="none"
              minW="180px"
              boxShadow="0px 2px 4px 0px #075A5A3D"
              maxH="235px"
              overflowY="scroll"
            >
              {dates.map((data) => (
                <MenuItem
                  key={data.key}
                  bg={data.key === selectedDate ? "#B6DFDF" : "inherit"}
                  py="6px"
                  px="10px"
                  onClick={() => setSelectedDate(data.key)}
                >
                  <Text
                    fontSize={{ base: "12px", md: "14px" }}
                    lineHeight="18px"
                    fontWeight={data.key === selectedDate ? "500" : "400"}
                    color="textSecondary"
                    _hover={{
                      color: "textPrimary",
                      fontWeight: "500",
                    }}
                  >
                    {data.value}
                  </Text>
                </MenuItem>
              ))}
            </MenuList>
          </Menu>
        </Flex>

        <Flex alignItems="center" gap="2px" color="textSecondary">
          <Text fontSize={{ base: "10px", md: "14px" }} fontWeight="400">
            APR:
          </Text>
          <Text fontSize={{ base: "16px", md: "18px" }} fontWeight="500">
            {avgAPY}%
          </Text>
        </Flex>
      </Flex>

      <Box display={{ base: "none", md: "block" }}>
        <Table>
          <Thead>
            <Tr bg="#D8F0F0">
              {TEXT_STRINGS.COLUMNS.map((field) => (
                <Th
                  key={field.key}
                  fontSize="12px"
                  lineHeight="14.4px"
                  fontWeight="400"
                  p={2}
                  textTransform="none"
                  isNumeric={field.isNumeric}
                  w={field.isNumeric ? "20%" : "inherit"}
                >
                  {field.name}
                </Th>
              ))}
            </Tr>
          </Thead>

          <Tbody>
            {allocations.map((data, index: number) => (
              <Tr
                key={data.strategy_name}
                bg={index % 2 !== 0 ? "#D8F0F0" : "inherit"}
              >
                {TEXT_STRINGS.COLUMNS.map((field) => (
                  <Td
                    key={field.key}
                    fontSize="12px"
                    lineHeight="14.4px"
                    fontWeight="400"
                    p={2}
                    textTransform="none"
                    isNumeric={field.isNumeric}
                    w={field.isNumeric ? "20%" : "inherit"}
                    borderBottom="0px"
                  >
                    <Flex
                      alignItems="center"
                      gap="8px"
                      justifyContent={field.isNumeric ? "flex-end" : "inherit"}
                    >
                      {field.hasImage && (
                        <Image
                          src={data["img_url"]}
                          width={24}
                          height={24}
                          alt=""
                        />
                      )}
                      <Text
                        fontSize="14px"
                        fontWeight="400"
                        lineHeight="24px"
                        color="textPrimary"
                      >
                        {data[field.key as keyof StrategyAllocationType]}
                        {field.isNumeric ? "%" : ""}
                      </Text>
                    </Flex>
                  </Td>
                ))}
              </Tr>
            ))}
          </Tbody>
        </Table>
      </Box>

      <Flex
        display={{ base: "inherit", md: "none" }}
        flexDir="column"
        gap="12px"
        alignItems="center"
      >
        {allocations.map((data) => (
          <Flex
            key={data.allocation}
            width="100%"
            flexDir="column"
            pt="12px"
            pb="8px"
            px="4px"
            bg="#D8F0F0"
            borderRadius="12px"
            gap="12px"
          >
            <Flex alignItems="center" gap="6px" px={2}>
              <Image src={data["img_url"]} width={24} height={24} alt="" />
              <Text fontSize="14px" color="textPrimary" fontWeight="500">
                {data["strategy_name"]}
              </Text>
            </Flex>

            <Flex alignItems="center" justifyContent="space-between">
              {TEXT_STRINGS.COLUMNS.filter(
                (data) => data.key !== "strategy_name"
              ).map((column, index) => (
                <Flex
                  key={data.strategy_name + column.key}
                  alignItems="center"
                  gap="8px"
                >
                  {index > 0 && (
                    <Divider
                      orientation="vertical"
                      borderColor="textTertiary"
                      borderWidth="1px"
                      height="16px"
                      bg="#B6DFDF"
                    />
                  )}
                  <Flex
                    flexDir="column"
                    gap="2px"
                    color="textPrimary"
                    flex={1}
                    px={1}
                    py={1}
                  >
                    <Text
                      fontSize="12px"
                      lineHeight="12px"
                      fontWeight="500"
                      color="textPrimary"
                    >
                      {data[column.key as keyof StrategyAllocationType]}
                      {column.isNumeric ? "%" : ""}
                    </Text>
                    <Text fontSize="10px" fontWeight="400" lineHeight="120%">
                      {column.name}
                    </Text>
                  </Flex>
                </Flex>
              ))}
            </Flex>
          </Flex>
        ))}
      </Flex>

      {note && (
        <Flex>
          <Text
            fontSize={{ base: "12px", md: "14px" }}
            color="textSecondary"
            lineHeight="20px"
            whiteSpace="pre-line"
          >
            Note: {note}
          </Text>
        </Flex>
      )}
    </Flex>
  );
};

export default VaultAprReports;
